import { PServed } from '@wui/im'
import dayjs, { unix } from 'dayjs'
import { computed, type MaybeRefOrGetter, toValue } from 'vue'

import { useLocalization } from '@/shared/composables/localization'
import { useStoreGuestStatus } from '@/shared/guest-status'
import { OwnStatus } from '@/shared/types/store'
import { isGuest } from '@/shared/utils/uri'

import { useStoreContactStatus } from '../store'

export const useContactStatusPresence = (
  pServed: MaybeRefOrGetter<PServed | undefined | null>,
) => {
  const { t, tc, currentLocale } = useLocalization()
  const { getStatusByPServed: getContactStatusByPServed } = useStoreContactStatus()
  const { getStatusByPServed: getGuestStatusByPServed } = useStoreGuestStatus()

  const resolvedPServed = computed(() => toValue(pServed) || undefined)

  const isGuestPServed = computed(() => {
    const id = resolvedPServed.value
    return id ? isGuest(id) : false
  })

  const statuses = computed(() => {
    const id = resolvedPServed.value
    if (!id) return undefined
    if (isGuestPServed.value) {
      return getGuestStatusByPServed(id)
    }
    return getContactStatusByPServed(id)
  })

  const showOnlineStatus = computed(() => {
    if (!resolvedPServed.value) return false
    if (isGuestPServed.value) {
      return statuses.value?.isOnline === true
    }
    return statuses.value?.status === OwnStatus.ONLINE
  })

  const lastSeen = computed(() => {
    if (!statuses.value) return undefined

    const value = statuses.value.lastSeen
    if (!value) return undefined

    if (dayjs().from(unix(value).tz(), true) === 'минута') {
      return tc('common.minutes', 1)
    }
    return dayjs().locale(currentLocale.value).from(unix(value).tz(), true)
  })

  const showOfflineStatus = computed(() => {
    if (isGuestPServed.value) {
      return statuses.value?.isOnline === false
    }
    return !!lastSeen.value
  })

  const hasCustomStatusMessage = computed(() => Boolean(statuses.value?.statusMessage?.trim()))

  const hasStatusLabel = computed(() => {
    if (showOnlineStatus.value) return true
    if (!isGuestPServed.value && statuses.value?.status === OwnStatus.ABSENT) return true
    if (!isGuestPServed.value && statuses.value?.status === OwnStatus.DO_NOT_DISTURB) return true
    if (lastSeen.value) return true
    if (hasCustomStatusMessage.value) return true
    return false
  })

  const title = computed(() => {
    if (showOnlineStatus.value) {
      return t('common.online')
    }

    const status = statuses.value?.status
    if (status === OwnStatus.ABSENT) {
      return t('common.absent')
    }
    if (status === OwnStatus.DO_NOT_DISTURB) {
      return t('common.dontDisturb')
    }
    if (lastSeen.value) {
      return t('common.wasOnlineIn', { lastSeen: lastSeen.value })
    }

    return ''
  })

  return {
    hasCustomStatusMessage,
    hasStatusLabel,
    isGuestPServed,
    lastSeen,
    showOfflineStatus,
    showOnlineStatus,
    statuses,
    title,
  }
}
